/**
 * Generic Sign-In modal — opened from the site header/navigation's "Sign
 * in" button for ANY visitor (not an agent-specific login screen).
 *
 * Contains all supported providers side-by-side (Google via Emergent,
 * Facebook via our own OAuth in `routes/facebook_auth.py`). Apple slot is
 * intentionally omitted per the current requirements — dropping it back in
 * is a one-block copy of the Facebook button.
 *
 * Google returns the visitor to the exact page they clicked "Sign in" from
 * — `ShopperAuthContext` (mounted app-wide) picks up the returned
 * `#session_id=...` fragment and exchanges it for a shopper session, same
 * as every other SSO gate on the site. Facebook remains an agent-only
 * sign-in (always lands on the agent dashboard) since there's no shopper
 * equivalent for it yet.
 *
 * Google is always available. Facebook only lights up when the backend
 * reports both `FACEBOOK_APP_ID` and `FACEBOOK_APP_SECRET` are set —
 * otherwise the button renders disabled with a "Not configured yet"
 * tooltip so admins know exactly what's missing.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, AlertTriangle } from "lucide-react";
import { useIntegrationVisibility } from "@/hooks/useIntegrationVisibility";

const API = getApiUrl();

// In-app / WebView detection — Google + Facebook OAuth both block these.
const isInAppBrowser = (ua) => {
  if (!ua) return false;
  return [
    /Instagram/i, /FBAN|FBAV|FBIOS|FB_IAB/i, /Messenger/i, /LinkedInApp/i,
    /Twitter|TwitterApp/i, /WhatsApp/i, /Telegram/i, /Snapchat/i, /Pinterest/i,
    /Line\//i, /MicroMessenger/i, /TikTok|Bytedance|musical_ly/i, /Slack/i,
    /; wv\)/i,
  ].some((re) => re.test(ua));
};

export function SignInModal({ open, onOpenChange, initialError = null }) {
  const [fbStatus, setFbStatus] = useState({ configured: false, app_id_set: false, app_secret_set: false });
  const [checking, setChecking] = useState(true);
  const [inApp, setInApp] = useState(false);
  const [fbError, setFbError] = useState(initialError);
  const { isEnabled } = useIntegrationVisibility();
  const googleSsoEnabled = isEnabled("google_sso");
  const facebookSsoEnabled = isEnabled("facebook_sso");

  useEffect(() => {
    // Keep the banner in sync when a URL-triggered error arrives after mount.
    setFbError(initialError);
  }, [initialError]);

  useEffect(() => {
    if (!open) return;
    setInApp(isInAppBrowser(navigator.userAgent));
    let cancelled = false;
    axios
      .get(`${API}/facebook-auth/status`)
      .then((r) => { if (!cancelled) setFbStatus(r.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [open]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Header sign-in lands on the Agent Dashboard, which exchanges the session itself.
    const redirect = `${window.location.origin}/property-admin/dashboard`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
  };

  const handleFacebook = () => {
    // Backend redirects to Facebook, then sets `agent_fb_session` cookie
    // in the callback and lands on the agent dashboard.
    setFbError(null);
    window.location.href = `${API}/facebook-auth/login`;
  };

  // Human-friendly explanation for sign-in failures. Google's own error
  // (e.g. a disabled account) is already a clear sentence from our
  // backend, so it's shown as-is. Facebook's raw errors are cryptic
  // ("It looks like this app isn't available"), so those get translated.
  const authErrorHint = (() => {
    if (!fbError) return null;
    if (fbError.provider === "google") {
      return { title: "Google sign-in didn't complete", body: fbError.desc || "Something went wrong. Please try again." };
    }
    const reason = (fbError.reason || fbError.error || "").toLowerCase();
    const desc = (fbError.desc || "").toLowerCase();
    // Prioritise permission-scoped hints — Facebook often sets
    // error_reason=user_denied even when the real cause is that the app
    // has no supported permissions enabled. Keyword-match the description
    // FIRST so admins get an actionable message.
    if (
      desc.includes("permission") ||
      desc.includes("supported permission") ||
      desc.includes("not available") ||
      desc.includes("isn't available") ||
      desc.includes("isnt available")
    ) {
      return {
        title: "Facebook app isn't fully approved yet",
        body: "The Facebook app needs public_profile and email permissions enabled in the Meta Developer Console before it can sign anyone in. Sign in with Google in the meantime.",
      };
    }
    if (reason === "user_denied" || desc.includes("user denied") || desc.includes("cancel")) {
      return {
        title: "Sign-in cancelled",
        body: "You backed out of Facebook's confirmation screen. Try again whenever you're ready.",
      };
    }
    if (reason === "access_denied") {
      return {
        title: "Facebook denied the sign-in",
        body: "This usually means the Facebook app is missing required permissions. Try Google in the meantime, or contact the site administrator.",
      };
    }
    return {
      title: "Facebook sign-in didn't complete",
      body: fbError.desc || "Something went wrong on Facebook's side. Please try again or use Google.",
    };
  })();
  const showAuthError = authErrorHint && (fbError?.provider === "google" ? googleSsoEnabled : facebookSsoEnabled);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="signin-modal" closeButtonTestId="signin-modal-close-btn">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Sign In</DialogTitle>
          <DialogDescription>
            Choose a provider to sign in and manage your listings.
          </DialogDescription>
        </DialogHeader>

        {inApp && (
          <div className="border-2 border-amber-400 bg-amber-50 p-3 rounded space-y-1 text-sm" data-testid="signin-webview-warning">
            <div className="flex gap-2 items-start">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Open in your browser to sign in</p>
                <p className="text-amber-800 text-xs leading-relaxed">
                  Google and Facebook block sign-in inside in-app previews. Open this page in <strong>Safari</strong> or <strong>Chrome</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        {showAuthError && (
          <div
            className="border-2 border-red-300 bg-red-50 p-3 rounded space-y-1 text-sm"
            data-testid="signin-auth-error"
            role="alert"
          >
            <div className="flex gap-2 items-start">
              <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-red-900">{authErrorHint.title}</p>
                <p className="text-red-800 text-xs leading-relaxed">{authErrorHint.body}</p>
                <button
                  type="button"
                  onClick={() => setFbError(null)}
                  className="mt-1 text-xs text-red-700 underline hover:no-underline"
                  data-testid="signin-auth-error-dismiss"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3 pt-2">
          {googleSsoEnabled && (
            <Button
              type="button"
              onClick={handleGoogle}
              disabled={inApp}
              className="w-full h-12 bg-white border border-border hover:bg-muted/50 text-foreground flex items-center justify-center gap-3 text-sm font-medium disabled:opacity-60"
              data-testid="signin-google-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Sign in with Google
            </Button>
          )}

          {facebookSsoEnabled && (
            <Button
              type="button"
              onClick={handleFacebook}
              disabled={inApp || checking || !fbStatus.configured}
              className="w-full h-12 bg-[#1877F2] hover:bg-[#166fe5] text-white flex items-center justify-center gap-3 text-sm font-medium disabled:opacity-60"
              data-testid="signin-facebook-btn"
              title={
                !fbStatus.configured
                  ? (fbStatus.app_id_set
                      ? "Facebook not fully configured yet — App Secret missing."
                      : "Facebook not configured yet.")
                  : ""
              }
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987H7.898v-2.89h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.987C18.343 21.128 22 16.991 22 12z" />
              </svg>
              {checking
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : fbStatus.configured
                ? "Continue with Facebook"
                : "Facebook — Not configured yet"}
            </Button>
          )}

          {!googleSsoEnabled && !facebookSsoEnabled && (
            <p className="text-sm text-center text-muted-foreground py-2" data-testid="signin-no-providers">
              Sign-in is currently unavailable. Please contact the site administrator.
            </p>
          )}

          {/* Apple slot intentionally omitted for now — keep this hint so the
              next person doesn't rewire from scratch:
              <Button data-testid="signin-apple-btn"> … Continue with Apple </Button>
          */}
        </div>

        <p className="text-xs text-center text-muted-foreground pt-2">
          New here? Sign in with any provider — we&apos;ll set up your account automatically.
        </p>
      </DialogContent>
    </Dialog>
  );
}

export default SignInModal;
